/**
 * Stitch pyramid tile-existence manifest ({morphle_id}_stitch_manifest.bin).
 * See morpho/stitching/stitch_manifest.py for the binary format.
 */

const MAGIC = 0x4846524D; // 'MRPH'
const HEADER_SIZE = 32;
const LEVEL_ENTRY_SIZE = 16;
const SUPPORTED_VERSION = 1;

export const getStitchManifestFilename = (morphleId) => `${morphleId}_stitch_manifest.bin`;

/**
 * @returns {{version, stitchedXMax, stitchedYMax, zMax, minLevel, maxLevel, builtAtUnixMs,
 *            levels: Record<number, {tilesX, tilesY, bitOffset, bitCount}>, bytes: Uint8Array}|null}
 */
export const parseStitchManifest = (arrayBuffer) => {
    if (!arrayBuffer || arrayBuffer.byteLength < HEADER_SIZE) return null;

    const view = new DataView(arrayBuffer);
    if (view.getUint32(0, true) !== MAGIC) return null;
    const version = view.getUint16(4, true);
    if (version !== SUPPORTED_VERSION) return null;

    const minLevel = view.getUint8(17);
    const maxLevel = view.getUint8(18);
    const levelTableOffset = view.getUint32(20, true);
    const numLevels = maxLevel - minLevel + 1;
    if (numLevels <= 0 || levelTableOffset + numLevels * LEVEL_ENTRY_SIZE > arrayBuffer.byteLength) return null;

    const levels = {};
    for (let i = 0; i < numLevels; i++) {
        const base = levelTableOffset + i * LEVEL_ENTRY_SIZE;
        const level = {
            tilesX: view.getUint32(base, true),
            tilesY: view.getUint32(base + 4, true),
            bitOffset: view.getUint32(base + 8, true),
            bitCount: view.getUint32(base + 12, true),
        };
        if (level.bitOffset + Math.ceil(level.bitCount / 8) > arrayBuffer.byteLength) return null;
        levels[minLevel + i] = level;
    }

    return {
        version,
        stitchedXMax: view.getUint32(8, true),
        stitchedYMax: view.getUint32(12, true),
        zMax: view.getUint8(16),
        minLevel,
        maxLevel,
        builtAtUnixMs: Number(view.getBigUint64(24, true)),
        levels,
        bytes: new Uint8Array(arrayBuffer),
    };
};

export const manifestTileExists = (manifest, morphleLevel, x, y) => {
    const level = manifest?.levels[morphleLevel];
    if (!level || x < 0 || y < 0 || x >= level.tilesX || y >= level.tilesY) return false;
    const idx = y * level.tilesX + x;
    return (manifest.bytes[level.bitOffset + (idx >> 3)] & (1 << (7 - (idx & 7)))) !== 0;
};
